<script setup lang="ts">
  import { VText } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import { fetchSummaryList } from "@/entities/statistics";
  import type { ISummaryListResponse } from "@/entities/statistics";
  import Card from "@/components/atoms/Card.vue";

  const { t } = useI18n();
  const router = useRouter();

  // The last 30 days, formatted as local time to match the report page date format.
  const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
  const dateTo = dayjs().add(1, "day").startOf("day").format(DATE_FORMAT);
  const dateFrom = dayjs().subtract(30, "day").startOf("day").format(DATE_FORMAT);

  const { data, isLoading } = useCachedResource<ISummaryListResponse>({
    namespace: CACHE_NAMESPACE.SummaryList,
    key: "last-30-days",
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchSummaryList({ size: 1000, dateFrom, dateTo }),
  });

  // List rows: title "Сводка за {dateTo}", description from the summary text.
  const items = computed(() =>
    (data.value?.items ?? []).map((item) => ({
      id: item.id,
      title: t(
        "page.briefs.itemTitle",
        { date: dayjs(item.dateTo).format("DD.MM.YYYY") },
        { default: "Сводка за {date}" },
      ),
      description: item.text ?? "",
    })),
  );

  function openBrief(id: number) {
    void router.push(`/stats/briefs/${id}`);
  }
</script>

<template>
  <div :class="$style.base">
    <h1>{{ $t("page.briefs.title", {}, { default: "Сводки за 30 дней" }) }}</h1>

    <section :class="$style.list">
      <div v-if="items.length" :class="$style.items">
        <Card v-for="item in items" :key="item.id" :class="$style.item" @click="openBrief(item.id)">
          <span :class="$style.itemTitle">{{ item.title }}</span>
          <span v-if="item.description" :class="$style.itemSubtitle">{{ item.description }}</span>
        </Card>
      </div>
      <div v-else-if="!isLoading" :class="$style.empty">
        <VText :class="$style.emptyText">
          {{ $t("page.briefs.empty", {}, { default: "Нет сводок за последние 30 дней." }) }}
        </VText>
      </div>
    </section>
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 16px 20px;

    overflow: auto;
    overscroll-behavior: contain;

    display: flex;
    gap: 24px;
    flex-direction: column;

    // Flex-item spacer that clears the fixed BottomNavigation on every engine.
    &::after {
      content: "";
      flex-shrink: 0;
      height: calc(57px + 32px + env(safe-area-inset-bottom, 0px));
    }
  }

  .list {
    width: 100%;
    flex: 1 0 auto;

    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    padding: 12px 8px;

    display: flex;
    flex-direction: column;
    gap: 2px;

    cursor: pointer;
  }

  .itemTitle {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--foreground);
  }

  .itemSubtitle {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--muted-foreground);
  }

  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--border);
  }

  .emptyText {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
</style>
